import { useEffect, useState } from "react";
import { useDashboardData, useTransactions } from "@vaulzo/core/hooks/usePortfolioQueries";
import PortfolioTab from "./PortfolioTab";
import ActivityTab from "./ActivityTab";
import EntryModal from "./EntryModal";

const TABS = [
  { value: "portfolio", label: "Portfolio" },
  { value: "activity", label: "Activity" },
];

const ALL = { broker: "all", type: "all" };

/**
 * Everything about the US-stocks portfolio lives in this one modal:
 * Portfolio (holdings and cash per broker, worked out from the entries)
 * and Activity (the entries themselves). Adding or editing an entry opens
 * a smaller modal inside this one.
 *
 * `asset` is the portfolio as the caller had it; the live copy comes from
 * the dashboard query, so totals update as soon as an entry is saved.
 */
export default function USStocksModal({ open, asset, initialTab = "portfolio", startWithNewEntry = false, onClose }) {
  const { data: dash } = useDashboardData();
  const live = (asset && dash?.assets?.find((a) => a.id === asset.id)) || asset;
  const { data: transactions = [], isLoading } = useTransactions(open ? asset?.id : null);
  const [tab, setTab] = useState(initialTab);
  const [filters, setFilters] = useState(ALL);
  // null: no entry modal · { tx: null }: a new entry · { tx }: editing tx
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setFilters(ALL);
    setEntry(startWithNewEntry ? { tx: null } : null);
  }, [open, initialTab, startWithNewEntry]);

  // The page behind stays put while this is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Escape closes the entry modal first, then this one. Popovers inside
  // (the ticker search) handle their own Escape and mark it handled.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (entry) setEntry(null);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, entry, onClose]);

  if (!open || !live) return null;
  const isActivity = tab === "activity";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(26,34,28,0.5)] px-5 font-instrument text-pf-ink antialiased [font-feature-settings:'tnum'_1] animate-vz-fade"
      style={{
        paddingTop: "max(32px, calc(var(--safe-top) + 16px))",
        paddingBottom: "max(32px, calc(var(--safe-bottom) + 16px))",
      }}
      onMouseDown={(e) => e.target === e.currentTarget && !entry && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={live.name}
        className="relative flex h-[min(720px,100%)] w-full max-w-[720px] flex-col overflow-hidden rounded-[18px] bg-pf-bg shadow-[0_32px_80px_rgba(20,40,28,0.35)] animate-pf-rise"
      >
        <header className="grid flex-none grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-pf-line bg-white py-[14px] pl-6 pr-4">
          <div className="flex min-w-0 flex-col gap-px">
            <div className="text-[11px] font-medium tracking-[0.14em] text-pf-faint">ASSET</div>
            <div className="truncate text-[15px] font-semibold">{live.name}</div>
          </div>
          <div className="flex gap-[2px] rounded-[10px] bg-pf-track p-[3px]">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={`rounded-lg px-[18px] py-[6px] text-[13px] font-semibold transition-all duration-150 ${
                  tab === t.value ? "bg-white text-pf-ink shadow-[0_1px_2px_rgba(20,40,28,0.12)]" : "text-pf-tab"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="h-8 w-8 rounded-lg text-[18px] leading-none text-pf-muted transition-colors hover:bg-pf-subtle hover:text-pf-ink"
            >
              ×
            </button>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {isActivity ? (
            <ActivityTab
              transactions={transactions}
              loading={isLoading}
              filters={filters}
              setFilters={setFilters}
              onSelect={(tx) => setEntry({ tx })}
            />
          ) : (
            <PortfolioTab asset={live} entryCount={transactions.length} />
          )}
        </main>

        {isActivity && (
          <footer className="flex flex-none justify-end border-t border-pf-line bg-white px-6 py-3">
            <button
              type="button"
              onClick={() => setEntry({ tx: null })}
              className="rounded-lg bg-pf-accent px-4 py-[9px] text-[13px] font-semibold text-white transition-colors hover:bg-pf-accent-hover"
            >
              + Add entry
            </button>
          </footer>
        )}

        {entry && (
          <EntryModal
            key={entry.tx?.id || "new"}
            asset={live}
            transactions={transactions}
            editing={entry.tx}
            onClose={() => setEntry(null)}
          />
        )}
      </div>
    </div>
  );
}
